import type { Flag, FlagSeverity } from "./common";

// TES-1001 part 1 (Ö-249, Ö-274) — the versioned risk-code list for F-01 and
// F-07, version v1, transcribed from the canonical Drive document
// "Ö-249 — Versionssatt riskkodlista F-01 och F-07 — v1"
// (1xWhdRfmqEF7htZmnwkTS2i8I8--Nkbn0RuRtm709S5Y), read 2026-09-30.
//
// What this file is, and is not:
//
// - Every numbered v1 rule (1–25) is here, with the class, priority and action
//   the document gives it. Nothing is added, dropped or re-graded. Where the
//   document names no priority, `priority` is null ("not stated in v1"), never
//   a value we picked.
// - Only a rule whose class is a medical/clinical signal RAISES a flag
//   (`raisesFlag`). Eligibility, identity, routing, evidence status, free
//   text and continuity ordering are v1 entries too, but the document says
//   explicitly that they are "not a risk flag"; each is mapped to the
//   behaviour that already implements it (`existing`) and evaluates nothing.
// - A rule fires only through `condition`: an exact question_id and exact
//   option values in the published form version. The document forbids
//   guessing an unknown question_id, so a rule whose question does not exist
//   in the published form has `condition: null` and can never fire.
// - The document's "explicitly pending" refinements are listed with
//   `status: "pending"` so the list shows them. A pending entry is never
//   evaluated, has no condition, and cannot block anything (launch rule,
//   TES-1022 / TES-1025). Their codes are ours: the document names the topics,
//   not codes.
// - No entry is acute. Acute signals keep their own verified path (the F-02
//   and check-in hard stops, CP-13); this list adds none.
//
// Each raised flag carries the list version and its code, priority and
// destination, and the submission row carries the form version it was
// answered against, which is what the document asks each rule to log.

export const RISK_CODE_LIST_VERSION = "v1" as const;

export type RiskCodeStatus = "active" | "pending";

// The document's classes, in its own order of appearance.
export type RiskCodeClass =
  | "clinical_stop_signal"
  | "medical_attention"
  | "missing_basic_information"
  | "eligibility_signal"
  | "identity_rule"
  | "routing"
  | "attention_information"
  | "clinical_attention"
  | "continuity_priority"
  | "medical_risk_flag"
  | "decision_attention"
  | "deviation_flag"
  | "evidence_status"
  | "display_only";

// "high, not acute" / "elevated" / "normal", as the document words them.
export type RiskPriority = "high" | "elevated" | "normal";

// The form keys the list covers: F-01 (the qualification quiz, stored as
// `f01`) and F-07 (the prescription transfer).
export type RiskCodeTemplateKey = "f01" | "prescription_transfer";

export interface RiskCodeCondition {
  // The exact answer key in the published form version.
  questionId: string;
  // The exact option values that fire the rule.
  anyOf: readonly string[];
  // Follow-up answers shown with the flag (e.g. her own description).
  showAlso?: readonly string[];
}

export interface RiskCodeEntry {
  code: string;
  status: RiskCodeStatus;
  form: "F-01" | "F-07" | "F-01/F-02";
  templateKey: RiskCodeTemplateKey | null;
  class: RiskCodeClass;
  raisesFlag: boolean;
  // Null when the rule has no question in the published form (never fires).
  condition: RiskCodeCondition | null;
  // The condition as the document states it, in English.
  conditionText: string;
  priority: RiskPriority | null;
  // What the patient is told. Null when the rule adds no patient copy; a
  // string names the EXISTING copy that already carries it.
  patientInstruction: string | null;
  // Where the finding goes. Null for pending entries.
  destination: string | null;
  responsibility: string | null;
  // No SLA is decided in v1 for any entry.
  sla: null;
  // Admin label (Swedish, the doctor reads it).
  label: string;
  // The code or behaviour that already implements the entry, or null when
  // the entry is new with this list.
  existing: string | null;
}

// Where every raised flag goes: the doctor's "Riskflaggor" list on the
// patient overview and in the meeting sheet. No queue routing, no escalation.
const DOCTOR_RISK_FLAGS = "doctor_risk_flags";
const DOCTOR = "doctor";

const F01_FORM_VERSION_NOTE = "F-01 bmi-quiz v10";
const F07_FORM_VERSION_NOTE = "F-07 prescription_transfer content v9";

export const RISK_CODE_LIST_V1: readonly RiskCodeEntry[] = [
  // --- F-01 -----------------------------------------------------------------
  {
    code: "GRAVID",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "clinical_stop_signal",
    raisesFlag: true,
    condition: { questionId: "reproductiveStatus", anyOf: ["pregnant"] },
    conditionText: "Reproductive status = pregnant",
    priority: "high",
    patientInstruction:
      "Existing F-01 early-stop reason 'reproductive_status.pregnant'",
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Gravid",
    existing:
      "routeF01 stop rule reproductive_status.pregnant (treatment start stopped)",
  },
  {
    code: "AMMAR",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "clinical_stop_signal",
    raisesFlag: true,
    condition: { questionId: "reproductiveStatus", anyOf: ["breastfeeding"] },
    conditionText: "Reproductive status = breastfeeding",
    priority: "high",
    patientInstruction:
      "Existing F-01 early-stop reason 'reproductive_status.breastfeeding'",
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Ammar",
    existing:
      "routeF01 stop rule reproductive_status.breastfeeding (treatment start stopped)",
  },
  {
    code: "FORSOKER_BLI_GRAVID",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "clinical_stop_signal",
    raisesFlag: true,
    condition: { questionId: "reproductiveStatus", anyOf: ["trying"] },
    conditionText: "Reproductive status = trying to get pregnant",
    priority: "high",
    patientInstruction:
      "Existing F-01 early-stop reason 'reproductive_status.trying'",
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Försöker bli gravid",
    existing:
      "routeF01 stop rule reproductive_status.trying (treatment start stopped)",
  },
  {
    code: "PLANERAR_GRAVIDITET",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "medical_attention",
    raisesFlag: true,
    condition: { questionId: "reproductiveStatus", anyOf: ["planning"] },
    conditionText:
      "Reproductive status = planning a pregnancy; individual assessment, no automatic rejection",
    priority: "normal",
    patientInstruction: "F-01 result page 'individual_planning' (TES-1161)",
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Planerar graviditet",
    existing: "f01ResultOutcome: individual_planning (meeting module)",
  },
  {
    code: "REPRODUKTIV_STATUS_OSAKER",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "medical_attention",
    raisesFlag: true,
    condition: { questionId: "reproductiveStatus", anyOf: ["unsure"] },
    conditionText:
      "Reproductive status = unsure; individual assessment, treatment start may not rest on an assumption",
    priority: null,
    patientInstruction: "F-01 result page 'individual_unsure' (TES-1161)",
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Osäker på reproduktiv status",
    existing: "f01ResultOutcome: individual_unsure (meeting module)",
  },
  {
    code: "REPRODUKTIV_STATUS_EJ_ANGIVEN",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "missing_basic_information",
    raisesFlag: true,
    condition: {
      questionId: "reproductiveStatus",
      anyOf: ["prefer_not_to_say"],
    },
    conditionText:
      "Reproductive status = prefers not to say; not an acute risk, the doctor decides whether it must be completed before a decision",
    priority: null,
    patientInstruction: "F-01 result page 'needs_more_info' (TES-1161)",
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Vill inte ange reproduktiv status",
    existing:
      "f01ResultOutcome: needs_more_info (no booking before completion)",
  },
  {
    code: "BMI_UNDER_GRANS",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "eligibility_signal",
    raisesFlag: false,
    condition: null,
    conditionText:
      "BMI below the limit; an eligibility/indication signal, not a risk flag",
    priority: null,
    patientInstruction: "Existing F-01 early-stop reason 'bmi.under_25'",
    destination: "F-01 early feedback; decision basis in the doctor's review",
    responsibility: DOCTOR,
    sla: null,
    label: "BMI under gränsen",
    existing:
      "routeF01 stop rule bmi.under_25; BMI shown in the overview facts",
  },
  {
    code: "UNDER_18_BANKID",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "identity_rule",
    raisesFlag: false,
    condition: null,
    conditionText:
      "Under 18 by BankID; an identity and eligibility rule, not a risk flag",
    priority: null,
    patientInstruction: "Existing age-gate copy",
    destination: "Stops the flow before the form",
    responsibility: null,
    sla: null,
    label: "Under 18 (BankID)",
    existing: "assertAdult (age gate before F-01 and the medical forms)",
  },
  {
    code: "RECEPTOVERTAG_VALT",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "routing",
    raisesFlag: false,
    condition: null,
    conditionText:
      "Prescription transfer chosen; patient group and routing, not a risk flag",
    priority: null,
    patientInstruction: null,
    destination: "Patient group PRESCRIPTION_TRANSFER",
    responsibility: null,
    sla: null,
    label: "Receptövertag valt",
    existing: "establishPatientGroup (prescriptionTransfer answer)",
  },
  {
    code: "TIDIGARE_AVSLUTAT_PGA_BIVERKNING",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "medical_attention",
    raisesFlag: true,
    condition: {
      questionId: "weightMedHistory",
      anyOf: ["stopped_side_effects"],
    },
    conditionText:
      "Stopped prescription weight treatment because of problems or side effects; show the answer, no automatic stop",
    priority: null,
    patientInstruction: null,
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label:
      "Tidigare avslutat viktbehandling på grund av besvär eller biverkningar",
    existing: null,
  },
  {
    code: "FRITEXT_ANNAT",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "attention_information",
    raisesFlag: false,
    condition: null,
    conditionText:
      "Any free text; shown as information to note, clinical priority only through a separate approved rule",
    priority: null,
    patientInstruction: null,
    destination: "Information att uppmärksamma",
    responsibility: DOCTOR,
    sla: null,
    label: "Fritext",
    existing: "TES-1001 part 2: attention texts (all free text, verbatim)",
  },
  // TES-1164 — the list's two pending entries PENDING_DIABETES_TYP_1 and
  // PENDING_MTC_MEN2, decided by Ö-383 (Emelie 2026-10-08): both are asked
  // in F-01, "Ja" is an automatic stop, and "Jag är osäker" / "Jag vet inte"
  // continue "med en tydlig läkarflagga; inget automatiskt stopp". Neither
  // answer has a priority in the decision, so none is stated here.
  {
    code: "DIABETES_TYP_1",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "clinical_stop_signal",
    raisesFlag: true,
    condition: { questionId: "type1Diabetes", anyOf: ["yes"] },
    conditionText: "Diagnosed with type 1 diabetes = yes; automatic stop",
    priority: null,
    patientInstruction: "F-01 result page 'stop_type_1_diabetes' (§13)",
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Diabetes typ 1",
    existing: "routeF01 stop rule type_1_diabetes (treatment start stopped)",
  },
  {
    code: "DIABETES_TYP_1_OSAKER",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "medical_attention",
    raisesFlag: true,
    condition: { questionId: "type1Diabetes", anyOf: ["unsure"] },
    conditionText:
      "Unsure whether diagnosed with type 1 diabetes; continues to F-02 with a clear doctor flag, no automatic stop",
    priority: null,
    patientInstruction: null,
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Osäker på om hon har diabetes typ 1",
    existing: null,
  },
  {
    code: "MTC_MEN2",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "clinical_stop_signal",
    raisesFlag: true,
    condition: { questionId: "mtcMen2", anyOf: ["yes"] },
    conditionText:
      "MTC or MEN 2 in the patient or a biological parent, sibling or child = yes; automatic stop",
    priority: null,
    patientInstruction: "F-01 result page 'stop_mtc_men2' (§14)",
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "MTC/MEN 2 hos patienten eller förstagradssläkting",
    existing: "routeF01 stop rule mtc_men2 (treatment start stopped)",
  },
  {
    code: "MTC_MEN2_VET_EJ",
    status: "active",
    form: "F-01",
    templateKey: "f01",
    class: "medical_attention",
    raisesFlag: true,
    condition: { questionId: "mtcMen2", anyOf: ["dont_know"] },
    conditionText:
      "Does not know about MTC or MEN 2 in herself or a first-degree relative; continues to F-02 with a clear doctor flag, no automatic stop",
    priority: null,
    patientInstruction: null,
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Vet inte om MTC/MEN 2 finns hos henne eller förstagradssläkting",
    existing: null,
  },
  // --- F-07 -----------------------------------------------------------------
  {
    code: "RECEPT_EJ_GILTIGT",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "clinical_attention",
    raisesFlag: true,
    condition: { questionId: "pt_valid_prescription", anyOf: ["nej"] },
    conditionText:
      "The prescription or order is no longer valid; the transfer cannot be approved without a new decision or completion",
    priority: null,
    patientInstruction: null,
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Receptet eller ordinationen är inte längre giltig",
    existing:
      "deriveTransferGapRisk already sorts it 'high' in the transfer queue (continuity only)",
  },
  {
    code: "RECEPT_GILTIGHET_OSAKER",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "clinical_attention",
    raisesFlag: true,
    condition: { questionId: "pt_valid_prescription", anyOf: ["osaker"] },
    conditionText:
      "Unsure whether the prescription is valid; the doctor checks the prescription basis",
    priority: null,
    patientInstruction: null,
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Osäker på om receptet är giltigt",
    existing: null,
  },
  {
    code: "RISK_BEHANDLINGSGLAPP",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "continuity_priority",
    raisesFlag: false,
    condition: null,
    conditionText:
      "The medication may run out before Ella answers, or the remaining time passes the continuity limit; sort the case higher, no automatic prescription or dose advice",
    priority: null,
    patientInstruction: null,
    destination: "Transfer queue ordering",
    responsibility: DOCTOR,
    sla: null,
    label: "Risk för behandlingsglapp",
    existing:
      "deriveTransferGapRisk (Ö-181 thresholds) orders the transfer queue",
  },
  {
    code: "DOS_ANDRAD_UTAN_LAKARE",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "medical_risk_flag",
    raisesFlag: true,
    condition: {
      questionId: "pt_self_dose_change",
      anyOf: ["ja"],
      showAlso: ["pt_self_dose_change_details"],
    },
    conditionText:
      "Changed the dose without a doctor; always show her description of the change",
    priority: null,
    patientInstruction: null,
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Har ändrat dosen utan läkare",
    existing: null,
  },
  {
    code: "BIVERKNING_ELLER_BESVAR",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "medical_attention",
    raisesFlag: true,
    // TES-1048 (Ö-281): F-07 v11's structured question. Yes or unsure raises
    // it and shows her description with it. The priority stays unstated
    // (medium, never red): a plain yes or a free text is never classified as
    // acute by itself, and the doctor assesses the rest.
    condition: {
      questionId: "pt_side_effects",
      anyOf: ["ja", "osaker"],
      showAlso: ["pt_side_effects_details"],
    },
    conditionText:
      "Side effects or problems (yes or unsure); always show her description. Mild = normal attention, clear daily impact or impact on food/fluid = elevated, severe or acute follows the separate acute protocol; the answer alone never makes it acute",
    priority: null,
    patientInstruction: null,
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Biverkning eller besvär",
    existing: null,
  },
  {
    code: "ONSKAD_BEHANDLINGS_ELLER_DOSANDRING",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "decision_attention",
    raisesFlag: false,
    condition: null,
    conditionText:
      "Wants a treatment or dose change; decision basis, not a risk flag, never an automatic dose instruction",
    priority: null,
    patientInstruction: null,
    destination: "Decision basis in the transfer review",
    responsibility: DOCTOR,
    sla: null,
    label: "Önskad behandlings- eller dosändring",
    existing: "Shown with the answers in the transfer review",
  },
  {
    code: "LAKEMEDEL_ANNAT",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "attention_information",
    raisesFlag: false,
    condition: null,
    conditionText:
      "Medication 'other'; the choice is not a risk flag, the mandatory free text is always shown",
    priority: null,
    patientInstruction: null,
    destination: "Information att uppmärksamma",
    responsibility: DOCTOR,
    sla: null,
    label: "Annat läkemedel",
    existing: "TES-1001 part 2: attention texts (pt_medication_other verbatim)",
  },
  {
    code: "VERIFIERAD_AVVIKELSE_MOT_RECEPTUNDERLAG",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "deviation_flag",
    raisesFlag: true,
    // Needs an uploaded document that was actually read and verified; the
    // system cannot read one, and may not assume a contradiction.
    // TES-1049 (Ö-281): its ONE source is the doctor marking a document as
    // deviating in the transfer review (prescription-evidence-review.ts).
    // `condition` stays null, so no form answer, AI or OCR can raise it.
    condition: null,
    conditionText:
      "Medication, strength or dose differs from a prescription document that could actually be read and verified; the system may not assume a contradiction, so it is the doctor's own check",
    priority: null,
    patientInstruction: null,
    destination: DOCTOR_RISK_FLAGS,
    responsibility: DOCTOR,
    sla: null,
    label: "Verifierad avvikelse mot receptunderlaget",
    existing:
      "TES-1049: the doctor's deviation marking on an uploaded prescription document in the transfer review",
  },
  {
    code: "RECEPTUNDERLAG_SAKNAS",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "evidence_status",
    raisesFlag: false,
    condition: null,
    conditionText:
      "Prescription document missing; mandatory missing evidence, not a risk flag",
    priority: null,
    patientInstruction: null,
    destination: "Evidence status",
    responsibility: DOCTOR,
    sla: null,
    label: "Receptunderlag saknas",
    existing: "Medical evidence status (required upload)",
  },
  {
    code: "TIDIGARE_PROVSVAR_SAKNAS",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "evidence_status",
    raisesFlag: false,
    condition: null,
    conditionText:
      "Earlier lab results not uploaded; evidence status, not a risk flag",
    priority: null,
    patientInstruction: null,
    destination: "Evidence status",
    responsibility: DOCTOR,
    sla: null,
    label: "Tidigare provsvar saknas",
    existing: "Medical evidence status 'not uploaded'",
  },
  {
    code: "TIDIGARE_PROVSVAR_EJ_GRANSKAT",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "evidence_status",
    raisesFlag: false,
    condition: null,
    conditionText:
      "Earlier lab results uploaded but not medically reviewed; evidence status, not a risk flag",
    priority: null,
    patientInstruction: null,
    destination: "Evidence status",
    responsibility: DOCTOR,
    sla: null,
    label: "Tidigare provsvar ej granskat",
    existing: "Medical evidence status 'uploaded, not reviewed'",
  },
  {
    code: "BLODTRYCK_ELLER_VILOPULS_SAKNAS",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "evidence_status",
    raisesFlag: false,
    condition: null,
    conditionText:
      "Blood pressure or resting pulse missing; missing basic information, not a risk flag",
    priority: null,
    patientInstruction: null,
    destination: "Evidence status",
    responsibility: DOCTOR,
    sla: null,
    label: "Blodtryck eller vilopuls saknas",
    existing: "Measurement shown as missing in the transfer review",
  },
  {
    code: "BLODTRYCK_ELLER_VILOPULS_REGISTRERAT",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "display_only",
    raisesFlag: false,
    condition: null,
    conditionText:
      "Blood pressure or resting pulse given; value, date and source always shown, no automatic thresholds",
    priority: null,
    patientInstruction: null,
    destination: "Dina värden and the transfer review",
    responsibility: DOCTOR,
    sla: null,
    label: "Blodtryck eller vilopuls registrerat",
    existing:
      "recordTransferReading (MeasurementSession) and the review's reading",
  },
  {
    code: "TIDIGARE_PROVSVAR_GAMMALT",
    status: "active",
    form: "F-07",
    templateKey: "prescription_transfer",
    class: "display_only",
    raisesFlag: false,
    condition: null,
    conditionText:
      "Old earlier lab results; no automatic flag or fixed age limit, show the date and age of the sample",
    priority: null,
    patientInstruction: null,
    destination: "Transfer review",
    responsibility: DOCTOR,
    sla: null,
    label: "Tidigare provsvar gammalt",
    existing: "Lab date shown with the uploaded results",
  },
  // --- Explicitly pending: listed, never evaluated ---------------------------
  ...(
    [
      [
        "PENDING_AKUT_PANKREATIT",
        "Acute rule for pancreatitis: which single symptoms or combinations suffice",
        "Pankreatit (väntar på Kirill)",
      ],
      [
        "PENDING_AKUT_GALLVAG_LEVER",
        "Acute rule for biliary or liver involvement: which single symptoms or combinations suffice",
        "Gallvägs- eller leverpåverkan (väntar på Kirill)",
      ],
      [
        "PENDING_AKUT_DEHYDRERING_SVIMNING",
        "Acute rule for dehydration or fainting: which single symptoms or combinations suffice",
        "Dehydrering eller svimning (väntar på Kirill)",
      ],
      [
        "PENDING_AKUT_ALLERGISK_REAKTION",
        "Acute rule for allergic reaction: which single symptoms or combinations suffice",
        "Allergisk reaktion (väntar på Kirill)",
      ],
      [
        "PENDING_KRAKNING_DIARRE_IHALLANDE",
        "Persistent vomiting or diarrhoea: frequency and duration definition",
        "Ihållande kräkning eller diarré (väntar på Kirill)",
      ],
      [
        "PENDING_FORSTOPPNING",
        "Constipation: frequency and duration definition",
        "Förstoppning (väntar på Kirill)",
      ],
      [
        "PENDING_BLODTRYCK_PULS_GRANSER",
        "Automatic blood pressure and pulse rules: final priority, re-measurement and combination rules",
        "Blodtrycks- och pulsgränser (väntar på Kirill)",
      ],
    ] as const
  ).map(
    ([code, conditionText, label]): RiskCodeEntry => ({
      code,
      status: "pending",
      form: "F-01/F-02",
      templateKey: null,
      class: "medical_attention",
      raisesFlag: false,
      condition: null,
      conditionText,
      priority: null,
      patientInstruction: null,
      destination: null,
      responsibility: null,
      sla: null,
      label,
      existing:
        "Unchanged: the existing acute protocol and manual doctor assessment apply meanwhile",
    }),
  ),
];

// The form versions the question_id mapping was checked against. A later
// form version that renames a key simply stops matching (no guessing).
export const RISK_CODE_V1_FORM_VERSIONS = {
  f01: F01_FORM_VERSION_NOTE,
  prescription_transfer: F07_FORM_VERSION_NOTE,
} as const;

// Sorting weight only; every entry here is non-acute. A priority the
// document does not state sorts as "medium", neither above a stated high nor
// below a stated normal.
const SEVERITY_FOR: Record<RiskPriority | "unstated", FlagSeverity> = {
  high: "high",
  elevated: "medium",
  unstated: "medium",
  normal: "low",
};

function answerMatches(value: unknown, anyOf: readonly string[]): boolean {
  if (typeof value === "string") return anyOf.includes(value);
  if (Array.isArray(value)) {
    return value.some((v) => typeof v === "string" && anyOf.includes(v));
  }
  return false;
}

// The flags the list raises for one submission. Only ACTIVE entries that
// raise a flag and have a mapped question are read; a template the list does
// not cover, a missing list, a missing answer or an unexpected answer shape
// raises nothing, so the submission behaves exactly as it did before the list
// existed. Never throws.
export function evaluateRiskCodes(
  templateKey: string,
  answers: unknown,
  list: readonly RiskCodeEntry[] | null | undefined = RISK_CODE_LIST_V1,
  listVersion: string = RISK_CODE_LIST_VERSION,
): Flag[] {
  try {
    if (!list || !answers || typeof answers !== "object") return [];
    const record = answers as Record<string, unknown>;
    const out: Flag[] = [];
    for (const entry of list) {
      if (entry.status !== "active" || !entry.raisesFlag) continue;
      if (entry.templateKey !== templateKey || !entry.condition) continue;
      const { questionId, anyOf, showAlso = [] } = entry.condition;
      if (!answerMatches(record[questionId], anyOf)) continue;
      out.push({
        rule: entry.code,
        severity: SEVERITY_FOR[entry.priority ?? "unstated"],
        // Never routes by itself: the finding is shown to the doctor.
        escalate: "none",
        fieldKeys: [questionId, ...showAlso],
        riskCode: entry.code,
        riskListVersion: listVersion,
        riskPriority: entry.priority,
        riskDestination: entry.destination ?? DOCTOR_RISK_FLAGS,
      });
    }
    return out;
  } catch {
    return [];
  }
}

// The admin label for a risk-coded rule id, or undefined.
export function riskCodeLabel(code: string): string | undefined {
  return RISK_CODE_LIST_V1.find((e) => e.code === code)?.label;
}

// Swedish priority words for the admin row.
export function riskPriorityLabel(
  priority: RiskPriority | null | undefined,
): string {
  if (priority === "high") return "hög, inte akut";
  if (priority === "elevated") return "förhöjd";
  if (priority === "normal") return "normal";
  return "ej angiven i v1";
}
